import React, { useMemo, useState, useContext, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import shortid from 'shortid';
import { Moment } from 'moment';
import omit from 'lodash/omit';

import useClickOutside from '../../../hooks/useClickOutside';
import useCloseOnScroll from '../../../hooks/useCloseOnScroll';

import NewTableContext from '../../NewTable/NewTableContext';
import { NewDatePickerProps } from '../../NewDatePicker/NewDatePicker';

import useDoppelganger, { renderFloatingBox } from '../hooks/useDoppelganger';
import Controller from '../Controller';

import { CalendarBody, CalendarContainer, CalendarSection } from './styles';
import CalendarButton from './Calendar/CalendarButton/CalendarButton';
import CalendarShortcuts from './Calendar/CalendarShortcuts/CalendarShortcuts';
import Calendar from './Calendar/Calendar';
import { FULL_DATE, SHORT_DATE } from './Calendar/Constants';

import {
  getActualDateValue,
  defineInputInitialValue,
  defineInternalInitialValue,
  getMask,
  getMomentDate,
} from './helpers';
import { formatInkDate, normalizeDate, getResolvedLocale } from '../../../helpers/datetime';
import { InkDateType } from '../../../helpers/datetime/types';

type ValueReturnType = {
  value: string;
  unformattedValue?: string;
};

type CustomDate = Date | Moment | string;

const valueReturn = ({ value, unformattedValue }: ValueReturnType) => {
  return {
    value: value || '',
    unformattedValue: unformattedValue || '',
  };
};

function CoreDatePicker(props: NewDatePickerProps) {
  const {
    id,
    value,
    format,
    locale: userLocale,
    mask: propsMask,
    input,
    calendar,
    shortcuts,
    align,
    disabled,
    invalid,
    onChange,
    width = 'half',
    className,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    ...otherProps
  } = props;

  // this will always default to {input: 'en-US', output: 'en-US'} if no locale is provided
  const name = useMemo(() => `${id}-${shortid.generate()}`, [id]);
  const locale = getResolvedLocale(userLocale);
  const themeContext = useContext(ThemeContext);

  const { scrollableWrapperRef } = useContext(NewTableContext);
  const alternatePortalContainer = props.portalContainer?.current;
  const [calendarOpen, setCalendarOpen, ref, self] = useClickOutside();
  const doppelganger = useDoppelganger(ref, scrollableWrapperRef, calendarOpen, alternatePortalContainer);

  useCloseOnScroll(calendarOpen, setCalendarOpen, scrollableWrapperRef?.current);

  // this logic guarantees that the calendar shows the correct controllers for each available format
  const mask = getMask(propsMask, format, locale);
  const dateMask = (!mask && format !== 'monthCondensed') || mask === 'date' ? FULL_DATE : SHORT_DATE;

  const [masking, setMasking] = useState<'date' | 'short-date' | undefined>(mask);

  // this is a date in one of the accepted ink formats that is used internally by the calendar
  const [selectedDate, setSelectedDate] = useState<InkDateType>(
    defineInternalInitialValue({ value, format, dateMask, locale }),
  );
  // this is a formatted date that only works for displaying the date to the user
  const formattedValue = React.useMemo(() => {
    return format ? defineInputInitialValue({ value, format, locale }) : value;
  }, [format, value, locale]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // TODO: move this to extractDateTimeInfo in a future PR
    const { isoString } = formatInkDate({ value: newValue, format, locale });
    const { dateParts, isValidDate } = normalizeDate({ value: newValue });

    if (format && isValidDate) {
      // we need to use date parts to create the date in order to not have timezone interfere with the dates
      // as when new Date() instances are created they take into account the timezone potentially changing the
      // date for people in other timezones. This will in turn incorrectly set the selected date internally.
      const newDate = new Date(dateParts.year, dateParts.month, dateParts.day);

      setSelectedDate(newDate);
      onChange(e, valueReturn({ value: newValue, unformattedValue: isoString }));
      return;
    }

    onChange(e, valueReturn({ value: newValue, unformattedValue: isoString }));
  };

  const safelyUpdateMask = cb => {
    // We're toggling the mask because there's something wrong with custom masking in `react-number-format`,
    // that is preventing changes from the outside, like a click on the calendar. This is a workaround.
    setMasking(undefined);
    cb();
    setTimeout(() => {
      setMasking(mask);
    }, 0);
  };

  const handleCalendarChange = (e: React.MouseEvent<HTMLButtonElement>, value: string) => {
    // TODO: move this into extractDateTimeInfo in a future PR
    const { formatted, isoString } = formatInkDate({ value, format, locale });

    if (format && formatted) {
      onChange(e, valueReturn({ value, unformattedValue: isoString }));
      return;
    }

    safelyUpdateMask(() => {
      onChange(e, valueReturn({ value, unformattedValue: isoString }));
    });
  };

  const onDateClick = (e: any, day: CustomDate) => {
    setCalendarOpen(false);
    setSelectedDate(day);

    // TODO: move this into extractDateTimeInfo in a future PR
    const { isoString } = formatInkDate({ value: day });

    if (format) {
      // TODO: move this into extractDateTimeInfo in a future PR
      const { formatted } = formatInkDate({ value: day, format, locale });
      onChange(e, valueReturn({ value: formatted, unformattedValue: isoString }));
      return;
    }

    safelyUpdateMask(() => {
      const momentDate = getMomentDate(day, dateMask).format(dateMask);
      onChange(e, valueReturn({ value: momentDate, unformattedValue: isoString }));
    });
  };

  useEffect(() => {
    const calendarNode = ref.current;
    const keyboardHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && calendarOpen) {
        setCalendarOpen(false);
      }
    };

    calendarNode?.addEventListener('keydown', keyboardHandler);

    return () => {
      calendarNode?.removeEventListener('keydown', keyboardHandler);
    };
  }, [calendarOpen, ref, setCalendarOpen]);

  // onDateClick is not supported by Controller.Wrapper as a pseudo custom event so we need to remove it
  // placeholder only applies to the input element, not its wrapper
  const controllerWrapperProps = omit(otherProps, ['placeholder', 'onDateClick']);

  return (
    <Controller.Wrapper
      ref={ref}
      id={`datepicker-id-${id}`}
      className={className}
      {...controllerWrapperProps}
      data-testid={dataTestId}
      theme={{ ...themeContext, controllerWidth: width }}
    >
      {input &&
        input({
          id,
          name,
          value: formattedValue,
          'aria-label': ariaLabel,
          mask: masking,
          disabled,
          invalid,
          onChange: handleChange,
          suffix: fieldContextError => (
            <CalendarButton
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
              disabled={disabled}
              invalid={invalid || fieldContextError}
            />
          ),
          width,
          placeholder: ' ',
          ...otherProps,
        })}
      {calendarOpen &&
        renderFloatingBox(
          doppelganger,
          <CalendarContainer align={align} ref={self}>
            <CalendarBody data-testid={dataTestId ? `${dataTestId}-box` : undefined}>
              {shortcuts && (
                <CalendarSection>
                  {shortcuts({
                    onDateClick,
                    closeCalendar: () => setCalendarOpen(false),
                  })}
                </CalendarSection>
              )}
              {calendar && (
                <CalendarSection>
                  {calendar({
                    value: getActualDateValue(value, format).toString(),
                    dateMask,
                    format,
                    locale,
                    closeCalendar: () => setCalendarOpen(false),
                    onChange: handleCalendarChange,
                    selectedDate,
                    setSelectedDate,
                    onDateClick,
                  })}
                </CalendarSection>
              )}
            </CalendarBody>
          </CalendarContainer>,
        )}
    </Controller.Wrapper>
  );
}

CoreDatePicker.Calendar = Calendar;
CoreDatePicker.Shortcuts = CalendarShortcuts;

CoreDatePicker.displayName = 'CoreDatePicker';

CoreDatePicker.defaultProps = {
  disabled: false,
  invalid: false,
  input: undefined,
  calendar: undefined,
  shortcuts: undefined,
  align: 'left',
};

export default CoreDatePicker;
